import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';

const TOKEN_EXPIRY_OFFSET_IN_SECONDS = 5 * 60; // token refresh is handled 5 minutes before expiry.

class IdentityService {

    private tokenTTL: number;
    private refreshTokenTTL: number;

    constructor() {
        this.tokenTTL = Number(window.localStorage.getItem('token_ttl'));
        this.refreshTokenTTL = Number(window.localStorage.getItem('refresh_token_ttl'));
    }

    /**
     * Create Auth Session
     * @returns Promise until session is available
     */
    createSession(login: string, password: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const apiEndpoint = '/identity/v0/login';
            const requestBody: DisguiseDto.IdentityAuthRequestDto = {
                login,
                password
            };
            HTTP_SERVICE_INSTANCE.getUnauthorizedAxios().post<DisguiseDto.IdentityAuthResponseDto>(
                apiEndpoint,
                requestBody
            ).then((response) => {
                this.updateAccessToken(response.data);
                this.updateRefreshToken(response.data);
                resolve();
            }).catch((error) => reject(error));
        });
    }

    /**
     * Create Auth Session
     * @returns Promise until session is available
     */
    createSessionUsingToken(token: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const apiEndpoint = '/identity/v0/tokenlogin';
            const requestBody: DisguiseDto.IdentityTokenAuthRequestDto = {
                token
            };
            HTTP_SERVICE_INSTANCE.getUnauthorizedAxios().post<DisguiseDto.IdentityAuthResponseDto>(
              apiEndpoint,
              requestBody
            ).then((response) => {
                this.updateAccessToken(response.data);
                this.updateRefreshToken(response.data);
                resolve();
            }).catch((error) => reject(error));
        });
    }

    isTokenExpired(): boolean {
        return Date.now() > this.tokenTTL;
    }

    isRefreshTokenExpired(): boolean {
        return Date.now() > this.refreshTokenTTL;
    }

    refreshToken() {
        return new Promise((resolve, reject) => {
            const apiEndpoint = '/identity/v0/refresh';
            HTTP_SERVICE_INSTANCE.getUnauthorizedAxios().post(apiEndpoint).then((response) => {
                this.updateAccessToken(response.data);
                resolve(undefined);
            }).catch((error) => reject(error));
        });
    }

    /**
     * Logout operation
     * @returns Promise that is resolved once session is properly destroyed.
     */
    destroySession() {
        window.localStorage.clear();
        return HTTP_SERVICE_INSTANCE.getAxios().post('/identity/v0/logout');
    }

    /**
     * Updates the Access Token TTL Information
     */
    private updateAccessToken(response: DisguiseDto.IdentityAuthResponseDto) {
        if (response.tokenTTL) {
            const currentTimestamp = Date.now();
            const tokenTTLDiffInMilliseconds = (response.tokenTTL - TOKEN_EXPIRY_OFFSET_IN_SECONDS) * 1000;
            this.tokenTTL = currentTimestamp + tokenTTLDiffInMilliseconds;
            window.localStorage.setItem('token_ttl', String(this.tokenTTL));
        }
    }

    /**
     * Updates the Refresh Token TTL Information
     */
    private updateRefreshToken(response: DisguiseDto.IdentityAuthResponseDto) {
        if (response.refreshTokenTTL) {
            const currentTimestamp = Date.now();
            const refreshTokenTTLDiffInMilliseconds = response.refreshTokenTTL * 1000;
            this.refreshTokenTTL = currentTimestamp + refreshTokenTTLDiffInMilliseconds;
            window.localStorage.setItem('refresh_token_ttl', String(this.refreshTokenTTL));
        }
    }
}

const IDENTITY_SERVICE_INSTANCE = new IdentityService();

export {IDENTITY_SERVICE_INSTANCE};
